// src/App.js
import { CssBaseline, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { themeSettings } from "theme";
import Layout from "scenes/layout";
import Dashboard from "scenes/dashboard";
import Admin from "scenes/admin";
import Performance from "scenes/performance";
import LoginPage from "scenes/login";
import ForcePasswordChangePage from "scenes/forcePasswordChange";
import useIdleLogout from "hooks/useIdleLogout";

// ✅ Apollo
import { ApolloProvider } from "@apollo/client";
import { userClient, accountClient, groupClient } from "state/apolloClient";

import Users from "scenes/users";
import MyTransactions from "scenes/transactions/MyTransactions";
import GroupTransactions from "scenes/transactions/GroupTransactions";
import GroupPositions from "scenes/groupPositions";
import GroupOfficials from "scenes/groupOfficials";
import ContributionSchedule from "scenes/contributions/ContributionSchedule";
import MyLoans from "scenes/loans/MyLoans";
import AllLoans from "scenes/loans/AllLoans";
import MyGuarantees from "scenes/loans/MyGuarantees";
import FineTypes from "scenes/fines/FineTypes";
import MyFines from "scenes/fines/MyFines";
import GroupFines from "scenes/fines/GroupFines";
import Groups from "scenes/groups";
import AdminSwitch from "scenes/adminSwitch";
import ContributionPolicy from "scenes/contributionPolicy";
import FundsDrives from "scenes/fundsDrives";
import FundsDriveDetails from "scenes/fundsDrives/FundsDriveDetails";

// Guard to require an auth token (from Redux or localStorage)
function RequireAuth({ children }) {
  const { isAuthenticated, token, requiresPasswordChange } = useSelector((s) => s.auth ?? {});
  const effectiveToken = token || localStorage.getItem("token");
  if (!isAuthenticated && !effectiveToken) {
    return <Navigate to="/login" replace />;
  }
  if (requiresPasswordChange) {
    return <Navigate to="/force-password-change" replace />;
  }
  return children;
}

function RequirePasswordChange({ children }) {
  const { isAuthenticated, token, requiresPasswordChange } = useSelector((s) => s.auth ?? {});
  const effectiveToken = token || localStorage.getItem("token");
  if (!isAuthenticated && !effectiveToken) return <Navigate to="/login" replace />;
  if (!requiresPasswordChange) return <Navigate to="/dashboard" replace />;
  return children;
}

function RequireSysAdmin({ children }) {
  const roleGroupId = localStorage.getItem("roleGroupId");
  if (roleGroupId !== "1") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRoutes() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ✅ Now this is inside <BrowserRouter />
  useIdleLogout({ timeoutMs: 20 * 60 * 1000, dispatch, navigate });

  return (
    <Routes>
      {/* ✅ Default landing route goes to login */}
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* Public route */}
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/force-password-change"
        element={
          <RequirePasswordChange>
            <ApolloProvider client={userClient}>
              <ForcePasswordChangePage />
            </ApolloProvider>
          </RequirePasswordChange>
        }
      />

      {/* Protected area */}
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route
          path="/dashboard"
          element={
            <ApolloProvider client={accountClient}>
              <Dashboard />
            </ApolloProvider>
          }
        />
        <Route path="/admin" element={<Admin />} />
        <Route path="/performance" element={<Performance />} />

        <Route
          path="/users"
          element={
            <ApolloProvider client={userClient}>
              <Users />
            </ApolloProvider>
          }
        />

        <Route
          path="/admin-switch"
          element={
            <RequireSysAdmin>
              <ApolloProvider client={groupClient}>
                <AdminSwitch />
              </ApolloProvider>
            </RequireSysAdmin>
          }
        />

        <Route
          path="/group-positions"
          element={
            <ApolloProvider client={groupClient}>
              <GroupPositions />
            </ApolloProvider>
          }
        />

        <Route
          path="/fines/types"
          element={
            <ApolloProvider client={groupClient}>
              <FineTypes />
            </ApolloProvider>
          }
        />

        <Route
          path="/groups"
          element={
            <RequireSysAdmin>
              <ApolloProvider client={groupClient}>
                <Groups />
              </ApolloProvider>
            </RequireSysAdmin>
          }
        />

        <Route
          path="/fines/my"
          element={
            <ApolloProvider client={groupClient}>
              <MyFines />
            </ApolloProvider>
          }
        />

        <Route
          path="/fines/group"
          element={
            <ApolloProvider client={groupClient}>
              <GroupFines />
            </ApolloProvider>
          }
        />

        <Route
          path="/group-officials"
          element={
            <ApolloProvider client={groupClient}>
              <GroupOfficials />
            </ApolloProvider>
          }
        />

        <Route
          path="/transactions/my"
          element={
            <ApolloProvider client={accountClient}>
              <MyTransactions />
            </ApolloProvider>
          }
        />

        <Route
          path="/transactions/group"
          element={
            <ApolloProvider client={accountClient}>
              <GroupTransactions />
            </ApolloProvider>
          }
        />

        <Route
          path="/contributions/my-schedule"
          element={
            <ApolloProvider client={groupClient}>
              <ContributionSchedule />
            </ApolloProvider>
          }
        />

        <Route
          path="/contributions/user/:userId"
          element={
            <ApolloProvider client={groupClient}>
              <ContributionSchedule />
            </ApolloProvider>
          }
        />

        <Route
          path="/loans/my"
          element={
            <ApolloProvider client={accountClient}>
              <MyLoans />
            </ApolloProvider>
          }
        />

        <Route
          path="/loans/all"
          element={
            <ApolloProvider client={accountClient}>
              <AllLoans />
            </ApolloProvider>
          }
        />

        <Route
          path="/loans/guarantees"
          element={
            <ApolloProvider client={accountClient}>
              <MyGuarantees />
            </ApolloProvider>
          }
        />

        <Route
          path="/contributions/policy"
          element={
            <ApolloProvider client={groupClient}>
              <ContributionPolicy />
            </ApolloProvider>
          }
        />

        <Route
          path="/funds-drives"
          element={
            <ApolloProvider client={accountClient}>
              <FundsDrives />
            </ApolloProvider>
          }
        />

        <Route
          path="/funds-drives/:driveId"
          element={
            <ApolloProvider client={accountClient}>
              <FundsDriveDetails />
            </ApolloProvider>
          }
        />
      </Route>

      {/* Optional: fallback */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  const mode = useSelector((state) => state.global.mode);
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  return (
    <div className="app">
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AppRoutes />
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
